import React, { useState, useEffect } from 'react';
import { Lock, ArrowRight, AlertOctagon, Clock } from 'lucide-react';

interface AdminLoginProps {
  onLogin: () => void;
  onCancel: () => void;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLogin, onCancel }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  // Initialize state from local storage on mount
  useEffect(() => {
    const storedAttempts = parseInt(localStorage.getItem('adminLoginAttempts') || '0', 10);
    const blockUntil = parseInt(localStorage.getItem('adminBlockUntil') || '0', 10);
    const now = Date.now();

    if (blockUntil && blockUntil > now) {
      // User is currently blocked
      setIsBlocked(true);
      setAttempts(5);
      setTimeLeft(Math.ceil((blockUntil - now) / 1000));
      setError('Access blocked due to multiple failed attempts.');
    } else {
      // Block expired or doesn't exist
      if (blockUntil && blockUntil <= now) {
        // Clear expired block
        localStorage.removeItem('adminBlockUntil');
        localStorage.removeItem('adminLoginAttempts');
        setAttempts(0);
      } else {
        setAttempts(storedAttempts);
      }
    }
  }, []);

  // Timer countdown effect
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (isBlocked && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          const newTime = prev - 1;
          if (newTime <= 0) {
            // Unblock when time runs out
            setIsBlocked(false);
            localStorage.removeItem('adminBlockUntil');
            localStorage.removeItem('adminLoginAttempts');
            setAttempts(0);
            setError('');
            return 0;
          }
          return newTime;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isBlocked, timeLeft]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isBlocked) return;

    if (pin === '012026') { 
      // Success
      localStorage.removeItem('adminLoginAttempts');
      localStorage.removeItem('adminBlockUntil');
      onLogin();
    } else {
      // Failed attempt
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      localStorage.setItem('adminLoginAttempts', newAttempts.toString());
      setPin('');

      if (newAttempts >= 5) {
        // Trigger Block
        setIsBlocked(true);
        const blockDuration = 30 * 60 * 1000; // 30 minutes in milliseconds
        const blockUntil = Date.now() + blockDuration;
        localStorage.setItem('adminBlockUntil', blockUntil.toString());
        setTimeLeft(30 * 60); // 30 minutes in seconds
        setError('Too many failed attempts. Access blocked for 30 minutes.');
      } else {
        const remaining = 5 - newAttempts;
        setError(`Invalid PIN. ${remaining} attempts remaining.`);
      }
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 pt-20">
      <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-gray-100">
        <div className="text-center mb-8">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isBlocked ? 'bg-red-100' : 'bg-black'}`}>
                {isBlocked ? (
                    <AlertOctagon className="text-red-600 w-8 h-8" />
                ) : (
                    <Lock className="text-white w-8 h-8" />
                )}
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
                {isBlocked ? 'Access Suspended' : 'Admin Access'}
            </h2>
            <p className="text-gray-500 mt-2">
                {isBlocked 
                    ? 'Security lockout active.' 
                    : 'Enter the 6-digit PIN to manage blog posts.'}
            </p>
        </div>
        
        {isBlocked ? (
             <div className="bg-red-50 border border-red-100 rounded-xl p-6 text-center mb-6">
                <Clock className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-red-800 font-bold text-lg mb-1">Try again in</p>
                <p className="text-3xl font-mono font-bold text-red-600">{formatTime(timeLeft)}</p>
                <p className="text-xs text-red-400 mt-4">Please contact support if you forgot your PIN.</p>
             </div>
        ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <input 
                        type="password" 
                        maxLength={6}
                        value={pin}
                        onChange={(e) => setPin(e.target.value)}
                        className={`w-full text-center text-3xl tracking-[0.5em] font-bold border-b-2 py-2 focus:outline-none transition-colors ${
                            error ? 'border-red-300 text-red-600 focus:border-red-500' : 'border-gray-200 focus:border-orange-500'
                        }`}
                        placeholder="••••••"
                        autoFocus
                        disabled={isBlocked}
                    />
                </div>
                {error && <p className="text-red-500 text-center text-sm font-medium animate-pulse">{error}</p>}
                
                <div className="flex gap-4">
                    <button 
                        type="button" 
                        onClick={onCancel}
                        className="flex-1 py-3 px-4 rounded-xl border border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button 
                        type="submit" 
                        className="flex-1 py-3 px-4 rounded-xl bg-black text-white font-bold hover:bg-gray-800 transition-colors flex items-center justify-center"
                    >
                        Access <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                </div>
            </form>
        )}
        
        {isBlocked && (
             <button 
                type="button" 
                onClick={onCancel}
                className="w-full mt-4 py-3 px-4 rounded-xl border border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition-colors"
            >
                Back to Home
            </button>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;